import { useState, useCallback, useEffect } from "react"
import { VStack, HStack, Text, Button, Spinner, Badge, Alert, Table, Box, Select } from "@chakra-ui/react"
import { FaInfoCircle } from "react-icons/fa"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { useFeatureToggles } from "../../hooks/useFeatureToggles"
import { useSelector } from "react-redux"
import { RootState } from "../../state/store"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"

interface Organization {
  id: string
  name: string
  slug: string
}

interface OrganizationFeaturesManagerProps {
  organization?: Organization
}

/**
 * Organization Features Manager Component
 *
 * Allows organization admins to override global feature settings for their organization.
 * Only shows features that allow organization-level overrides.
 */
export const OrganizationFeaturesManager = ({ organization }: OrganizationFeaturesManagerProps) => {
  const {
    definitions,
    global: globalToggles,
    overrides,
    definitionsLoading,
    overridesLoading,
    definitionsError,
    overridesError,
    refreshDefinitions,
    refreshOverrides,
    refreshGlobal,
    toggleOrganizationFeature,
  } = useFeatureToggles()

  const activeOrganization = useSelector((state: RootState) => state.section.activeOrganization)
  const [updatingFeatures, setUpdatingFeatures] = useState<Set<string>>(new Set())
  const [selectedOrgId, setSelectedOrgId] = useState<string>("")

  // Set default organization - use passed organization or active organization
  useEffect(() => {
    if (organization && !selectedOrgId) {
      setSelectedOrgId(organization.id)
    } else if (activeOrganization && !selectedOrgId) {
      setSelectedOrgId(activeOrganization.organization.id)
    }
  }, [organization, activeOrganization, selectedOrgId])

  // Load data when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      refreshDefinitions()
      refreshGlobal()
      refreshOverrides(selectedOrgId)
    }
  }, [selectedOrgId, refreshDefinitions, refreshGlobal, refreshOverrides])

  const handleToggleFeature = useCallback(
    async (featureKey: string, newEnabled: boolean) => {
      if (!selectedOrgId) {
        toaster.error({
          title: "No organization selected",
          description: "Please select an organization first",
        })
        return
      }

      setUpdatingFeatures((prev) => new Set(prev).add(featureKey))

      try {
        await toggleOrganizationFeature(featureKey, newEnabled, selectedOrgId)
        toaster.success({
          title: "Feature updated",
          description: `${featureKey} is now ${newEnabled ? "enabled" : "disabled"} for this organization`,
        })
      } catch (error) {
        console.error("Failed to update feature:", error)
        toaster.error({
          title: "Failed to update feature",
          description: error instanceof Error ? error.message : "Unknown error",
        })
      } finally {
        setUpdatingFeatures((prev) => {
          const newSet = new Set(prev)
          newSet.delete(featureKey)
          return newSet
        })
      }
    },
    [toggleOrganizationFeature, selectedOrgId]
  )

  const handleRefresh = useCallback(() => {
    refreshDefinitions()
    refreshGlobal()
    refreshOverrides(selectedOrgId)
  }, [refreshDefinitions, refreshGlobal, refreshOverrides, selectedOrgId])

  const loading = definitionsLoading || overridesLoading
  const error = definitionsError || overridesError

  // Filter definitions to only show features that allow org overrides
  const overridableDefinitions = definitions.filter((def) => def.allowOrgOverride)

  // Combine definitions with override states
  // Filter overrides to only those belonging to the selected org
  const orgOverrides = overrides.filter((o) => o.organizationId === selectedOrgId)

  const featuresWithStates = overridableDefinitions.map((definition) => {
    const override = orgOverrides.find((o) => o.featureKey === definition.featureKey)
    const globalToggle = globalToggles.find((g) => g.featureKey === definition.featureKey)
    const globalEnabled = globalToggle?.enabled ?? definition.defaultEnabled
    return {
      ...definition,
      hasOverride: !!override,
      overrideEnabled: override?.enabled ?? false,
      effectiveEnabled: override ? override.enabled : globalEnabled,
      globalEnabled,
    }
  })

  if (error) {
    return (
      <Alert.Root status="error">
        <Alert.Content>
          <Alert.Title>Failed to load organization features</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  return (
    <VStack gap={6} align="stretch">
      <HStack justify="space-between">
        <VStack align="start" gap={1}>
          <Text fontSize="2xl" fontWeight="bold">
            Organization Feature Overrides
          </Text>
          <Text fontSize="sm" color="fg.muted">
            Override global feature settings for specific organizations
          </Text>
        </VStack>
        <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Refresh"}
        </Button>
      </HStack>

      {/* Organization Selector - only show if no specific organization is passed */}
      {!organization && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Organization
          </Text>
          <Select.Root
            value={selectedOrgId}
            onValueChange={(details) => setSelectedOrgId(details.value[0] || "")}
            disabled={!activeOrganization}
          >
            <Select.Trigger>
              <Select.ValueText placeholder="Select organisation" />
            </Select.Trigger>
            <Select.Content>
              {activeOrganization && (
                <Select.Item value={activeOrganization.organization.id}>
                  {activeOrganization.organization.name}
                </Select.Item>
              )}
            </Select.Content>
          </Select.Root>
        </Box>
      )}

      {/* Show selected organization when passed as prop */}
      {organization && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Managing features for:
          </Text>
          <Text fontSize="lg" fontWeight="bold">
            {organization.name}
          </Text>
        </Box>
      )}

      <Alert.Root>
        <Alert.Content>
          <HStack>
            <FaInfoCircle />
            <VStack align="start" gap={1}>
              <Alert.Title>Organization Feature Overrides</Alert.Title>
              <Alert.Description>
                These settings override global feature toggles for the selected organization. Only features that allow
                organization-level overrides are shown here.
              </Alert.Description>
            </VStack>
          </HStack>
        </Alert.Content>
      </Alert.Root>

      {loading ? (
        <HStack justify="center" py={8}>
          <Spinner size="lg" />
          <Text>Loading organization features...</Text>
        </HStack>
      ) : featuresWithStates.length === 0 ? (
        <Alert.Root>
          <Alert.Content>
            <Alert.Title>No overridable features</Alert.Title>
            <Alert.Description>
              No features are currently configured to allow organization-level overrides.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : (
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Feature</Table.ColumnHeader>
              <Table.ColumnHeader>Category</Table.ColumnHeader>
              <Table.ColumnHeader>Override Status</Table.ColumnHeader>
              <Table.ColumnHeader>Override Value</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {featuresWithStates.map((feature) => {
              const isUpdating = updatingFeatures.has(feature.featureKey)

              return (
                <Table.Row key={feature.id}>
                  <Table.Cell>
                    <VStack align="start" gap={1}>
                      <Text fontWeight="medium">{feature.name}</Text>
                      <Text fontSize="sm" color="fg.muted">
                        {feature.description}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Key: {feature.featureKey}
                      </Text>
                    </VStack>
                  </Table.Cell>

                  <Table.Cell>
                    <Badge variant="subtle" colorPalette="blue">
                      {feature.category}
                    </Badge>
                  </Table.Cell>

                  <Table.Cell>
                    <Badge
                      variant={feature.hasOverride ? "subtle" : "outline"}
                      colorPalette={feature.hasOverride ? "orange" : "gray"}
                    >
                      {feature.hasOverride ? "Overridden" : "Using Global"}
                    </Badge>
                  </Table.Cell>

                  <Table.Cell>
                    {feature.hasOverride ? (
                      <Badge variant="subtle" colorPalette={feature.overrideEnabled ? "green" : "red"}>
                        {feature.overrideEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    ) : (
                      <Badge variant="outline" colorPalette={feature.globalEnabled ? "green" : "red"}>
                        {feature.globalEnabled ? "Enabled" : "Disabled"} (Global)
                      </Badge>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    <HStack gap={2}>
                      <Switch
                        checked={feature.effectiveEnabled}
                        disabled={isUpdating || !selectedOrgId}
                        onCheckedChange={(details) => handleToggleFeature(feature.featureKey, details.checked)}
                      />
                      {isUpdating && <Spinner size="sm" />}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      )}
    </VStack>
  )
}
